import { ARENA_ZONES, GAME, OBSTACLES } from './config';
import {
  consume,
  createDelivery,
  createInventory,
  deliver,
  drawDelivery,
  updateOverflow,
} from './inventory';
import { SeededRandom } from './rng';
import { SpatialGrid } from './spatialGrid';
import {
  DROP_TYPES,
  type Core,
  type DeliveryState,
  type DropType,
  type FieldEffect,
  type InventoryState,
  type MatchPhase,
  type MatchStats,
  type SimulationEvent,
  type Team,
  type Unit,
} from './types';

const TAU = Math.PI * 2;

function emptyDropStats(): Record<DropType, number> {
  return Object.fromEntries(DROP_TYPES.map((type) => [type, 0])) as Record<DropType, number>;
}

function distanceSquared(ax: number, ay: number, bx: number, by: number): number {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy;
}

function normalize(x: number, y: number): { x: number; y: number; length: number } {
  const length = Math.hypot(x, y);
  if (length < 0.0001) return { x: 0, y: 0, length: 0 };
  return { x: x / length, y: y / length, length };
}

export class Simulation {
  readonly random: SeededRandom;
  readonly grid = new SpatialGrid(GAME.neighborRadius);
  readonly obstacles = OBSTACLES;
  readonly zones = ARENA_ZONES;
  readonly inventories: [InventoryState, InventoryState];
  readonly cores: [Core, Core];
  readonly stats: MatchStats;
  readonly events: SimulationEvent[] = [];
  delivery: DeliveryState;
  units: Unit[] = [];
  effects: FieldEffect[] = [];
  phase: MatchPhase = 'ready';
  time = 0;
  winner: Team | null = null;
  suddenDeath = false;
  private nextUnitId = 1;
  private nextEffectId = 1;
  private spawnAt: [number, number] = [0, 0];
  private aiThinkAt = 1;

  constructor(readonly seed = 0x50484552) {
    this.random = new SeededRandom(seed);
    this.inventories = [createInventory(), createInventory()];
    this.delivery = createDelivery(this.random);
    this.cores = [
      { team: 0, x: 72, y: GAME.height / 2, health: GAME.coreHealth, maxHealth: GAME.coreHealth },
      { team: 1, x: GAME.width - 72, y: GAME.height / 2, health: GAME.coreHealth, maxHealth: GAME.coreHealth },
    ];
    this.stats = {
      spawned: [0, 0],
      lost: [0, 0],
      coreHits: [0, 0],
      dropsUsed: { 0: emptyDropStats(), 1: emptyDropStats() },
    };
  }

  start(): void {
    if (this.phase === 'ready' || this.phase === 'paused') this.phase = 'playing';
  }

  pause(): void {
    if (this.phase === 'playing') this.phase = 'paused';
  }

  drainEvents(): SimulationEvent[] {
    return this.events.splice(0, this.events.length);
  }

  step(dt = GAME.fixedDt): void {
    if (this.phase !== 'playing') return;
    this.time += dt;
    this.updateDelivery();
    updateOverflow(this.inventories[0], this.time);
    updateOverflow(this.inventories[1], this.time);
    this.updateSpawns();
    this.updateEffects();
    this.updateUnits(dt);
    this.updateZones(dt);
    this.resolveCombat();
    this.updateAi();
    this.updateWinState();
  }

  useDrop(team: Team, type: DropType, x: number, y: number): boolean {
    if (this.phase !== 'playing') return false;
    if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
    const clampedX = Math.max(20, Math.min(GAME.width - 20, x));
    const clampedY = Math.max(20, Math.min(GAME.height - 20, y));
    if (!consume(this.inventories[team], type)) return false;

    this.stats.dropsUsed[team][type] += 1;
    this.events.push({ type: 'drop', team, drop: type, x: clampedX, y: clampedY });
    if (type === 'surge') {
      for (let i = 0; i < GAME.effect.surge.count; i += 1) this.spawnUnit(team, true);
      return true;
    }

    const duration = GAME.effect[type].duration;
    this.effects.push({
      id: this.nextEffectId++,
      type,
      team,
      x: clampedX,
      y: clampedY,
      createdAt: this.time,
      expiresAt: this.time + duration,
      triggered: false,
    });
    return true;
  }

  private updateDelivery(): void {
    while (this.time >= this.delivery.nextAt) {
      const type = drawDelivery(this.delivery, this.random);
      deliver(this.inventories[0], type, this.time);
      deliver(this.inventories[1], type, this.time);
      this.events.push({ type: 'delivery', drop: type });
    }
  }

  private updateSpawns(): void {
    for (const team of [0, 1] as const) {
      while (this.time >= this.spawnAt[team] && this.units.length < GAME.maxUnits) {
        this.spawnUnit(team, false);
        this.spawnAt[team] += GAME.spawnInterval;
      }
    }
  }

  private spawnUnit(team: Team, surge: boolean): void {
    if (this.units.length >= GAME.maxUnits) return;
    const core = this.cores[team];
    const direction = team === 0 ? 1 : -1;
    const angle = this.random.range(-Math.PI * 0.7, Math.PI * 0.7);
    const spread = surge ? this.random.range(28, 58) : this.random.range(22, 42);
    const x = core.x + direction * 36 + Math.cos(angle) * spread * 0.35;
    const y = core.y + Math.sin(angle) * spread;
    this.units.push({
      id: this.nextUnitId++,
      team,
      x,
      y,
      vx: direction * GAME.unitSpeed * this.random.range(0.75, 1.05),
      vy: this.random.range(-10, 10),
      health: GAME.unitHealth,
      weapon: false,
      shield: false,
      decayExposure: 0,
      inForgeZone: false,
      inShieldZone: false,
      contactCooldownUntil: 0,
    });
    this.stats.spawned[team] += 1;
  }

  private updateEffects(): void {
    for (const effect of this.effects) {
      if (
        effect.type === 'explosion' &&
        !effect.triggered &&
        this.time >= effect.createdAt + GAME.effect.explosion.warning
      ) {
        effect.triggered = true;
        const radiusSq = GAME.effect.explosion.radius ** 2;
        const survivors: Unit[] = [];
        let eliminated = 0;
        for (const unit of this.units) {
          if (distanceSquared(unit.x, unit.y, effect.x, effect.y) <= radiusSq) {
            if (unit.shield) {
              unit.shield = false;
              this.events.push({ type: 'shield-break', team: unit.team, x: unit.x, y: unit.y, count: 1 });
              survivors.push(unit);
            } else {
              this.stats.lost[unit.team] += 1;
              eliminated += 1;
            }
          } else survivors.push(unit);
        }
        this.units = survivors;
        this.events.push({ type: 'explosion', team: effect.team, drop: 'explosion', x: effect.x, y: effect.y, count: eliminated });
      }
    }
    this.effects = this.effects.filter((effect) => this.time < effect.expiresAt);
  }

  private updateZones(dt: number): void {
    const forge = this.zones.find((zone) => zone.type === 'forge');
    const shield = this.zones.find((zone) => zone.type === 'shield');
    const decay = this.zones.find((zone) => zone.type === 'decay');
    if (!forge || !shield || !decay) return;

    const survivors: Unit[] = [];
    for (const unit of this.units) {
      const inForge = distanceSquared(unit.x, unit.y, forge.x, forge.y) <= forge.radius ** 2;
      const inShield = distanceSquared(unit.x, unit.y, shield.x, shield.y) <= shield.radius ** 2;
      const inDecay = distanceSquared(unit.x, unit.y, decay.x, decay.y) <= decay.radius ** 2;

      if (inForge && !unit.inForgeZone) {
        unit.weapon = true;
        this.events.push({ type: 'zone-charge', zone: 'forge', team: unit.team, x: unit.x, y: unit.y, count: 1 });
      }
      if (inShield && !unit.inShieldZone) {
        unit.shield = true;
        this.events.push({ type: 'zone-charge', zone: 'shield', team: unit.team, x: unit.x, y: unit.y, count: 1 });
      }
      unit.inForgeZone = inForge;
      unit.inShieldZone = inShield;

      if (inDecay) {
        unit.decayExposure += dt;
        if (unit.decayExposure >= GAME.decayDuration) {
          if (unit.shield) {
            unit.shield = false;
            unit.decayExposure = 0;
            this.events.push({ type: 'shield-break', zone: 'decay', team: unit.team, x: unit.x, y: unit.y, count: 1 });
          } else {
            this.stats.lost[unit.team] += 1;
            this.events.push({ type: 'decay', zone: 'decay', team: unit.team, x: unit.x, y: unit.y, count: 1 });
            continue;
          }
        }
      } else {
        unit.decayExposure = 0;
      }
      survivors.push(unit);
    }
    this.units = survivors;
  }

  private updateUnits(dt: number): void {
    this.grid.rebuild(this.units);
    for (const unit of this.units) {
      const enemyCore = this.cores[unit.team === 0 ? 1 : 0];
      const goal = normalize(enemyCore.x - unit.x, enemyCore.y - unit.y);
      let steerX = goal.x * 52;
      let steerY = goal.y * 52;
      let cohesionX = 0;
      let cohesionY = 0;
      let alignX = 0;
      let alignY = 0;
      let neighbors = 0;

      for (const other of this.grid.nearby(unit.x, unit.y)) {
        if (other.id === unit.id || other.team !== unit.team) continue;
        const dx = other.x - unit.x;
        const dy = other.y - unit.y;
        const distSq = dx * dx + dy * dy;
        if (distSq > GAME.neighborRadius ** 2 || distSq < 0.001) continue;
        neighbors += 1;
        cohesionX += other.x;
        cohesionY += other.y;
        alignX += other.vx;
        alignY += other.vy;
        if (distSq < 18 ** 2) {
          const inverse = 1 / Math.max(2, Math.sqrt(distSq));
          steerX -= dx * inverse * 68;
          steerY -= dy * inverse * 68;
        }
      }

      if (neighbors > 0) {
        steerX += ((cohesionX / neighbors - unit.x) * 0.72 + (alignX / neighbors - unit.vx) * 0.17);
        steerY += ((cohesionY / neighbors - unit.y) * 0.72 + (alignY / neighbors - unit.vy) * 0.17);
      }

      let speedMultiplier = 1;
      for (const effect of this.effects) {
        const dx = effect.x - unit.x;
        const dy = effect.y - unit.y;
        const dist = Math.hypot(dx, dy);
        if (effect.type === 'attractor' && effect.team === unit.team && dist < GAME.effect.attractor.radius) {
          const strength = (1 - dist / GAME.effect.attractor.radius) * GAME.effect.attractor.force;
          const dir = normalize(dx, dy);
          steerX += dir.x * strength;
          steerY += dir.y * strength;
        } else if (effect.type === 'deflector' && dist < GAME.effect.deflector.radius) {
          const strength = (1 - dist / GAME.effect.deflector.radius) * GAME.effect.deflector.force;
          const dir = normalize(-dx, -dy);
          steerX += dir.x * strength;
          steerY += dir.y * strength;
        } else if (effect.type === 'speed' && effect.team === unit.team && dist < GAME.effect.speed.radius) {
          speedMultiplier = GAME.effect.speed.multiplier;
        }
      }

      for (const obstacle of this.obstacles) {
        const awayX = unit.x - obstacle.x;
        const awayY = unit.y - obstacle.y;
        const dist = Math.hypot(awayX, awayY);
        const influence = obstacle.radius + 34;
        if (dist < influence) {
          const force = (1 - dist / influence) * 370;
          const dir = normalize(awayX, awayY);
          steerX += dir.x * force;
          steerY += dir.y * force;
        }
      }

      unit.vx += steerX * dt;
      unit.vy += steerY * dt;
      const maxSpeed = GAME.unitSpeed * speedMultiplier;
      const velocity = normalize(unit.vx, unit.vy);
      if (velocity.length > maxSpeed) {
        unit.vx = velocity.x * maxSpeed;
        unit.vy = velocity.y * maxSpeed;
      }
      unit.x += unit.vx * dt;
      unit.y += unit.vy * dt;
      this.constrainUnit(unit);
    }
  }

  private constrainUnit(unit: Unit): void {
    const margin = GAME.unitRadius + 4;
    if (unit.x < margin || unit.x > GAME.width - margin) {
      unit.x = Math.max(margin, Math.min(GAME.width - margin, unit.x));
      unit.vx *= -0.65;
    }
    if (unit.y < margin || unit.y > GAME.height - margin) {
      unit.y = Math.max(margin, Math.min(GAME.height - margin, unit.y));
      unit.vy *= -0.65;
    }
    for (const obstacle of this.obstacles) {
      const dx = unit.x - obstacle.x;
      const dy = unit.y - obstacle.y;
      const dist = Math.hypot(dx, dy);
      const minimum = obstacle.radius + GAME.unitRadius;
      if (dist < minimum && dist > 0.001) {
        unit.x = obstacle.x + (dx / dist) * minimum;
        unit.y = obstacle.y + (dy / dist) * minimum;
        unit.vx += (dx / dist) * 12;
        unit.vy += (dy / dist) * 12;
      }
    }
  }

  private resolveCombat(): void {
    this.grid.rebuild(this.units);
    const dead = new Set<number>();
    const collisionSq = GAME.collisionRadius ** 2;

    for (const unit of this.units) {
      if (dead.has(unit.id) || this.time < unit.contactCooldownUntil) continue;
      let target: Unit | undefined;
      for (const other of this.grid.nearby(unit.x, unit.y)) {
        if (other.team === unit.team || dead.has(other.id) || this.time < other.contactCooldownUntil) continue;
        if (distanceSquared(unit.x, unit.y, other.x, other.y) > collisionSq) continue;
        if (!target || other.id < target.id) target = other;
      }
      if (target) {
        const hitX = (unit.x + target.x) / 2;
        const hitY = (unit.y + target.y) / 2;
        unit.contactCooldownUntil = this.time + GAME.contactCooldown;
        target.contactCooldownUntil = this.time + GAME.contactCooldown;

        const unitStrikes = unit.weapon;
        const targetStrikes = target.weapon;
        unit.weapon = false;
        target.weapon = false;

        if (unitStrikes) {
          if (target.shield) {
            target.shield = false;
            this.events.push({ type: 'shield-break', team: target.team, x: target.x, y: target.y, count: 1 });
          } else dead.add(target.id);
        }
        if (targetStrikes) {
          if (unit.shield) {
            unit.shield = false;
            this.events.push({ type: 'shield-break', team: unit.team, x: unit.x, y: unit.y, count: 1 });
          } else dead.add(unit.id);
        }

        if (!dead.has(unit.id) && !dead.has(target.id)) {
          if (unit.shield) {
            unit.shield = false;
            this.events.push({ type: 'shield-break', team: unit.team, x: unit.x, y: unit.y, count: 1 });
          } else unit.health = Math.max(0, unit.health - GAME.contactDamage);
          if (target.shield) {
            target.shield = false;
            this.events.push({ type: 'shield-break', team: target.team, x: target.x, y: target.y, count: 1 });
          } else target.health = Math.max(0, target.health - GAME.contactDamage);
          if (unit.health <= 0) dead.add(unit.id);
          if (target.health <= 0) dead.add(target.id);
          this.events.push({ type: 'combat-hit', x: hitX, y: hitY, count: 2 });
        }

        const direction = normalize(target.x - unit.x, target.y - unit.y);
        const nx = direction.length > 0 ? direction.x : unit.team === 0 ? 1 : -1;
        const ny = direction.length > 0 ? direction.y : 0;
        unit.vx = -nx * GAME.contactBounce;
        unit.vy = -ny * GAME.contactBounce;
        target.vx = nx * GAME.contactBounce;
        target.vy = ny * GAME.contactBounce;

        let eliminated = 0;
        if (dead.has(unit.id)) {
          this.stats.lost[unit.team] += 1;
          eliminated += 1;
        }
        if (dead.has(target.id)) {
          this.stats.lost[target.team] += 1;
          eliminated += 1;
        }
        if (eliminated > 0) this.events.push({ type: 'elimination', x: hitX, y: hitY, count: eliminated });
      }
    }

    for (const unit of this.units) {
      if (dead.has(unit.id)) continue;
      const enemyTeam: Team = unit.team === 0 ? 1 : 0;
      const core = this.cores[enemyTeam];
      if (distanceSquared(unit.x, unit.y, core.x, core.y) <= GAME.coreRadius ** 2) {
        dead.add(unit.id);
        core.health = Math.max(0, core.health - 1);
        this.stats.lost[unit.team] += 1;
        this.stats.coreHits[unit.team] += 1;
        this.events.push({ type: 'core-hit', team: unit.team, x: core.x, y: core.y, count: 1 });
      }
    }

    if (dead.size > 0) this.units = this.units.filter((unit) => !dead.has(unit.id));
  }

  private updateAi(): void {
    if (this.time < this.aiThinkAt) return;
    this.aiThinkAt = this.time + GAME.aiThinkInterval;
    const inventory = this.inventories[1];
    if (inventory.items.length === 0) return;

    const playerUnits = this.units.filter((unit) => unit.team === 0);
    const aiUnits = this.units.filter((unit) => unit.team === 1);
    const has = (type: DropType) => inventory.items.includes(type);

    if (has('explosion') && playerUnits.length >= 4) {
      const target = this.findDensest(playerUnits);
      if (target.count >= 4 && this.useDrop(1, 'explosion', target.x, target.y)) return;
    }
    if (has('deflector')) {
      const threatened = playerUnits.filter((unit) => unit.x > GAME.width * 0.68);
      if (threatened.length >= 3) {
        const target = this.average(threatened);
        if (this.useDrop(1, 'deflector', target.x, target.y)) return;
      }
    }
    if (has('surge') && aiUnits.length < 42 && this.useDrop(1, 'surge', this.cores[1].x, this.cores[1].y)) return;
    if (has('attractor') && aiUnits.length >= 3) {
      const forge = this.zones.find((zone) => zone.type === 'forge');
      const shield = this.zones.find((zone) => zone.type === 'shield');
      const armed = aiUnits.filter((unit) => unit.weapon).length;
      const protectedUnits = aiUnits.filter((unit) => unit.shield).length;
      const target = armed <= protectedUnits ? forge : shield;
      if (target && this.useDrop(1, 'attractor', target.x, target.y)) return;
    }
    if (has('speed') && aiUnits.length >= 4) {
      const target = this.average(aiUnits);
      this.useDrop(1, 'speed', target.x, target.y);
    }
  }

  private findDensest(units: readonly Unit[]): { x: number; y: number; count: number } {
    let best = { x: GAME.width / 2, y: GAME.height / 2, count: 0 };
    for (let i = 0; i < units.length; i += Math.max(1, Math.floor(units.length / 24))) {
      const candidate = units[i];
      if (!candidate) continue;
      let count = 0;
      for (const unit of units) {
        if (distanceSquared(candidate.x, candidate.y, unit.x, unit.y) < GAME.effect.explosion.radius ** 2) count += 1;
      }
      if (count > best.count) best = { x: candidate.x, y: candidate.y, count };
    }
    return best;
  }

  private average(units: readonly Unit[]): { x: number; y: number } {
    if (units.length === 0) return { x: GAME.width / 2, y: GAME.height / 2 };
    let x = 0;
    let y = 0;
    for (const unit of units) {
      x += unit.x;
      y += unit.y;
    }
    return { x: x / units.length, y: y / units.length };
  }

  private updateWinState(): void {
    if (this.cores[0].health <= 0) return this.finish(1);
    if (this.cores[1].health <= 0) return this.finish(0);

    if (!this.suddenDeath && this.time >= GAME.matchDuration) {
      if (this.cores[0].health !== this.cores[1].health) {
        return this.finish(this.cores[0].health > this.cores[1].health ? 0 : 1);
      }
      this.suddenDeath = true;
    }
    if (this.suddenDeath && this.time >= GAME.matchDuration + GAME.suddenDeathDuration) {
      const score0 = this.cores[0].health * 1000 + this.units.filter((unit) => unit.team === 0).length;
      const score1 = this.cores[1].health * 1000 + this.units.filter((unit) => unit.team === 1).length;
      this.finish(score0 >= score1 ? 0 : 1);
    }
  }

  private finish(winner: Team): void {
    this.phase = 'finished';
    this.winner = winner;
    this.events.push({ type: 'finish', team: winner });
  }
}

export function effectRadius(type: Exclude<DropType, 'surge'>): number {
  return GAME.effect[type].radius;
}

export function effectProgress(effect: FieldEffect, now: number): number {
  return Math.max(0, Math.min(1, (now - effect.createdAt) / (effect.expiresAt - effect.createdAt)));
}

export function angleForUnit(unit: Unit): number {
  return Math.atan2(unit.vy, unit.vx) % TAU;
}
